import { Haptics, ImpactStyle } from '@capacitor/haptics'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import * as React from 'react'
import { cn } from '#app/utils/misc.js'

const button = cva(
	'inline-flex scale-100 active:scale-95 transition-all duration-75 items-center text-sm justify-center whitespace-nowrap rounded-full font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 transition-opacity',
	{
		variants: {
			variant: {
				default:
					'bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg',
				destructive:
					'bg-destructive text-destructive-foreground hover:bg-destructive/90',
				'destructive-soft':
					'bg-destructive/15 hover:bg-destructive/25 text-destructive',
				outline:
					'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
				'outline-primary':
					'border-2 border-primary/60 bg-transparent text-primary hover:bg-transparent hover:font-normal hover:text-primary hover:font-bold font-bold',
				secondary:
					'bg-secondary text-secondary-foreground hover:bg-secondary/80',
				ghost: 'hover:bg-accent hover:text-accent-foreground',
				link: 'text-primary underline-offset-4 hover:underline px-0.5 py-0',
				sidebar: 'bg-foreground/15 text-foreground hover:bg-foreground/10',
				success: 'bg-success text-success-foreground hover:bg-success/90',
			},
			size: {
				default: 'h-12 px-4 py-2 text-lg',
				sm: 'h-9 px-3',
				lg: 'h-16 px-7 text-xl',
				icon: 'h-10 w-10',
				'icon-sm': 'h-8 w-8',
				'icon-lg': 'h-12 w-12',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
)

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof button> {
	asChild?: boolean
	isLoading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ isLoading, className, variant, size, asChild = false, ...props }, ref) => {
		return asChild ? (
			<Slot
				className={cn(button({ variant, size }), className)}
				ref={ref}
				disabled={isLoading}
				{...props}
			/>
		) : (
			<button
				className={cn(button({ variant, size }), className)}
				ref={ref}
				disabled={isLoading}
				{...props}
				onClick={e => {
					Haptics.impact({ style: ImpactStyle.Light })
					props.onClick?.(e)
				}}
			>
				<>
					{isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
					{props.children}
				</>
			</button>
		)
	},
)
Button.displayName = 'Button'

export { Button, button }
